<script lang="ts">
	import LightshowThumbnail from "../../../file-system/tiles/LightshowThumbnail.svelte"
	import {
		DataHandlerLightshow,
		DataHandlerScene,
		Device,
		DeviceGroup,
		DeviceSupports,
		Lightshow,
		Scene,
		Tag,
		type FullCalendarEvent,
	} from "luxedo-data"
	import { ShowOverviewController } from "../ShowOverviewController"
	import { Toast } from "toaster"
	import { DateTime } from "luxon"
	import ProjectTags from "../../../tags/ProjectTags.svelte"
	import {
		ContextMenuButton,
		type ContextMenuOptions,
		triggerContextMenuWithGenerator,
	} from "context-menu"
	import { CTXOptions } from "../../../../../modules/ctx-menu/ContextMenuOptions"
	import ShowOverviewHeaderPlayOnProjector from "./ShowOverview_PlayOnProjector.svelte"
	import ProjectorIcon from "../../../icons/ProjectorIcon.svelte"
	import ShowOverviewAssignDevice from "./ShowOverview_AssignDevice.svelte"
	import { Responsive } from "responsive"
	import { DeviceStatusPreview } from "luxedo-comps"
	import ShowPreview from "../preview/ShowPreview.svelte"
	import ShowOverviewScheudule from "./ShowOverview_Scheudule.svelte"
	import PreviewInBrowserIcon from "../../../icons/PreviewInBrowserIcon.svelte"

	let show: Scene | Lightshow = $state()
	let event: FullCalendarEvent
	let device: Device = $state()
	let tags: Array<number> = $state([])

	let isMobile = $derived(Responsive.isMobile)
	let supportsPlayNow = $derived(DeviceSupports.playNow(device))

	ShowOverviewController.subscribe((ctx) => {
		show = ctx.show
		event = ctx.event
		device = ctx.device

		tags = show?.tags ?? []
	})

	async function toggleTag(tag: Tag) {
		await show.toggleTag(tag)
		tags = show.tags
	}

	function onSceneNameChange(
		e: KeyboardEvent & {
			currentTarget: EventTarget & HTMLHeadingElement
		},
	) {
		if (e.key === "Enter") e.currentTarget.blur()
	}

	async function saveSceneName(
		e: FocusEvent & {
			currentTarget: EventTarget & HTMLHeadingElement
		},
	) {
		const newName = e.currentTarget.textContent
		if (newName === show?.name) return

		try {
			if (show instanceof Scene) {
				await DataHandlerScene.rename(show, newName)
			} else {
				await DataHandlerLightshow.rename(show, newName)
			}
			Toast.success("Show title updated successfully! ")
		} catch (e) {
			console.error("[ERROR] While renaming scene", e)
			Toast.error(
				"An error occurred while updating the title of your show. Please refresh and try again. ",
			)
		}
	}

	function generateCtxOpts(e) {
		let opts: ContextMenuOptions = []
		// Added back preview button for lightshows so that I can work on building the new preview for lightshows
		opts.push({
			title: "Preview",
			onClick: ShowOverviewController.showVideoPlayer,
		})

		opts = opts.concat(
			show instanceof Scene
				? CTXOptions.Scene.createOptions(show, {
						fromOverlay: true,
						triggerReupload: ShowOverviewController.showReuploadForm,
					})
				: CTXOptions.Lightshow.createOptions(show, {
						fromOverlay: true,
						triggerReupload: ShowOverviewController.showReuploadForm,
					}),
		)

		return opts
	}
</script>

<div class="heading-container" class:isMobile>
	<div class="internal-heading-container">
		<ContextMenuButton onClick={triggerContextMenuWithGenerator(generateCtxOpts)} />

		<div class="left-container">
			<div class="tile">
				{#if show instanceof Lightshow}
					<LightshowThumbnail lightshow={show} />
				{:else}
					<img src={show?.src?.thumbnail} alt="{show?.name} Thumbnail" />
				{/if}
			</div>
			<div class="heading">
				<div class="lightshow-info">
					<div class="left">
						<h3
							id="show-preview-title"
							spellcheck="false"
							onkeydown={onSceneNameChange}
							onblur={saveSceneName}
							contenteditable={true}
						>
							{show?.name === "__temp_ls__"
								? `Temporary playlist for ${DateTime.fromJSDate(show?.updated_at).toFormat(
										"LLL dd, yyyy",
									)}`
								: (show?.name ?? "")}
						</h3>
					</div>
				</div>
				<div class="tag-container">
					<ProjectTags
						alignLeft
						selectedTags={tags}
						onToggle={toggleTag}
						isLibraryContext={false}
					/>
				</div>
			</div>
		</div>

		<div class="right">
			<div class="button-container lightshow-actions">
				{#if device}
					<button
						id="preview-in-browser-button"
						class="icon small"
						onclick={ShowOverviewController.showVideoPlayer}
					>
						<PreviewInBrowserIcon />
					</button>
					{#if supportsPlayNow}
						<ShowOverviewHeaderPlayOnProjector {show} {device} />
					{:else}
						<ShowOverviewScheudule {show} {device} />
					{/if}
				{/if}
			</div>
			<div class="flex-column assigned-device-info-container">
				{#if !isMobile}
					<p class="duration-info device-type">
						{device instanceof DeviceGroup ? "Group" : "Projector"}:
					</p>
				{/if}
				<div class="flex-row assigned-device-info">
					{#if device}
						<ProjectorIcon {device} />
						<DeviceStatusPreview {device} showControls={false} />
					{:else}
						<ShowOverviewAssignDevice />
					{/if}
				</div>
			</div>
		</div>
	</div>
	<ShowPreview />
</div>

<style>
	.heading-container {
		background-color: var(--color-bg-dark);
		backdrop-filter: blur(3rem);
		padding: 2rem;
		--tile-width: 8rem;
	}

	.internal-heading-container {
		display: flex;
		flex-direction: column;
		transition: transform;
		transition-duration: 500ms;
		transition-delay: 250ms;
	}

	:global(.isMobile) .internal-heading-container {
		flex-direction: column;
	}

	.heading {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		overflow: hidden;
	}

	.left-container {
		display: flex;
		flex-direction: row;
		flex-grow: 1;
		height: fit-content;
	}

	:global(.isMobile) .tile {
		--tile-width: 5rem;
	}

	.left-container {
		overflow: hidden;
	}

	.tile {
		min-width: var(--tile-width);
		width: var(--tile-width);
		height: var(--tile-width);
	}

	.button-container {
		width: fit-content;
		flex-wrap: nowrap;
		margin-bottom: 0;
	}

	.lightshow-info {
		height: fit-content;
		margin-left: 1rem;
		overflow: hidden;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-end;
	}

	:global(.isMobile) #show-preview-title {
		font-size: var(--h1);
	}

	.left {
		width: 100%;
	}

	.right {
		display: flex;
		flex-direction: row-reverse;
		justify-content: space-between;
		align-items: flex-end;
		padding-top: 1rem;
		width: 100%;
		align-items: center;
	}

	#preview-in-browser-button {
		width: 1.75rem;
		height: auto;
	}

	:global(.isMobile) #preview-in-browser-button {
		width: 1.25rem;
	}

	.right .flex-row :global(.projector-icon) {
		margin-right: 1rem;
	}

	:global(.isMobile) .right {
		/* align-items: flex-start; */
		flex-direction: row-reverse;
		padding-top: 1rem;
		width: 100%;
		margin-bottom: 0;
		align-items: center;
	}

	:global(.isMobile) .heading-container {
		padding: 1rem;
	}

	:global(.isMobile) .button-container {
		margin: 0;
	}

	:global(.isMobile) .assigned-device-info {
		line-height: 1.25;
	}

	.duration-info {
		color: var(--color-text);
		margin-bottom: 0;
		line-height: 1.25em;
	}

	:global(.isMobile) .duration-info {
		font-size: var(--h3);
	}

	.tag-container {
		margin: 1rem 0 0 1rem;
		padding-right: 0;
	}

	.tag-container :global(#scene-tags) {
		margin-right: 0;
	}

	.lightshow-actions {
		justify-content: flex-end;
	}

	:global(.isMobile) .tag-container {
		margin-top: 0.25rem;
		padding-right: 0;
	}

	.lightshow-info h3 {
		width: 100%;
		font-size: 3rem;
		margin: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	}

	.internal-heading-container :global(.actions) {
		position: absolute;
		top: 1.3rem;
		right: 3.5rem;
	}

	:global(.isMobile) .internal-heading-container :global(.actions) {
		position: absolute;
		top: 1rem;
		right: 2.5rem;
	}

	.assigned-device-info-container {
		overflow: hidden;
	}

	.assigned-device-info-container,
	.assigned-device-info {
		width: 100%;
	}

	.assigned-device-info {
		align-items: center;
	}

	.assigned-device-info :global(.device-title-row .device-name) {
		font-size: 1rem;
	}

	.assigned-device-info :global(.device-title-row .projector-status) {
		font-size: var(--text-small);
	}

	.device-type {
		margin-bottom: 0.5rem;
	}

	:global(.isMobile) .assigned-device-info-container {
		padding-right: 0.5rem;
	}

	:global(.isMobile) .assigned-device-info-container :global(.projector-icon) {
		margin-left: 0;
		margin-right: 0.5rem;
	}

	:global(.isMobile) .assigned-device-info-container :global(.status-preview .device-name) {
		line-height: 1.25em;
	}

	:global(.isMobile) .assigned-device-info-container :global(.status-preview .projector-status) {
		line-height: 1.25em;
	}

	:global(.isMobile) .assigned-device-info-container :global(.status-preview .device-name) {
		font-size: var(--h2);
	}

	.lightshow-actions :global(.actions) {
		position: absolute;
		top: 1rem;
		right: 2.5rem;
	}
</style>
